import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {DOCUMENT, Inject, Injectable} from '@angular/core';
import {
    ConverterService,
    HttpErrorModel,
    InterceptorUtil,
    LoggerService,
    Occ,
    OccEndpointsService,
    tryNormalizeHttpError,
    USE_CLIENT_TOKEN,
} from '@spartacus/core';
import {OccUserProfileAdapter} from '@spartacus/user/profile/occ';
import {Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {
    AimoB2BUnitOption,
    AimoBulletinList,
    AimoContactUs,
    AimoRegistration,
    AimoUser,
    AimoUserRegistrationData,
    B2BUnitOption,
    KitchenAlcoholInfo,
    OpeningHour,
} from '../../model/user.model';
import {openCloseSpinner} from "../../cms-components/shared/utils/spinner/aimo-spinner-utils";
import {AIMO_PROPAGATE_HTTP_ERROR} from "../../shared/http-interceptors/errors-http-interceptor";
import User = Occ.User;


@Injectable({
    providedIn: 'root',
})
export class AimoOccUserProfileAdapter extends OccUserProfileAdapter {
    constructor(
        protected http: HttpClient,
        protected occEndpoints: OccEndpointsService,
        protected converter: ConverterService,
        protected logger: LoggerService,
        protected recaptchaV3Service: ReCaptchaV3Service,
        @Inject(DOCUMENT) private document: Document,
    ) {
        super(http, occEndpoints, converter);
    }

    searchUnit(userId: string, term: string): Observable<B2BUnitOption[]> {
        const url = this.occEndpoints.buildUrl('searchUnits', {urlParams: {userId}, queryParams: {term: term}});
        return this.http.get<B2BUnitOption[]>(url);
    }

    selectUnit(userId: string, uid: string, fromSearch: boolean): Observable<User> {
        const url = this.occEndpoints.buildUrl('selectUnit', {
            urlParams: {userId},
            queryParams: {uid, fromSearch},
        });
        return this.http.get<User>(url);
    }

    toggleFavorite(userId: string, productPk: number): Observable<void> {
        const url = this.occEndpoints.buildUrl('toggleFavorite', {
            urlParams: {userId: userId, productPk: productPk},
        });
        return this.http.put<void>(url, null);
    }

    setPassword(token: string, newPassword: string): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('userSetPassword');
        const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, this.getContentType());

        return this.http
            .post(url, {token, newPassword}, {headers})
            .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
    }

    contactUs(body: AimoContactUs): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('contactUs');
        const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, this.getContentType());

        return this.http
            .post(url, body, {headers})
            .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
    }

    registerCustomer(userId: string, data: AimoRegistration): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('registerCustomer');
        let headers = this.getContentType();
        headers = InterceptorUtil.createHeader(AIMO_PROPAGATE_HTTP_ERROR, true, headers);

        return this.http
            .post(url, data, {headers})
            .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
    }

    addOrModifyUser(userId: string, data: AimoUserRegistrationData): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('addOrModifyUser', {urlParams: {userId}});
        const headers = this.getContentType();
        return this.http
            .post(url, data, {headers})
            .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
    }

    modifyB2BUnit(userId: string, data: AimoB2BUnitOption): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('modifyB2BUnit', {urlParams: {userId}});
        const headers = this.getContentType();
        return this.http
            .post(url, data, {headers})
            .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
    }

    getMyOrganizationDetails(userId: string): Observable<AimoUser> {
        const url = this.occEndpoints.buildUrl('user', {
            urlParams: {userId},
            queryParams: {fetchOrganizationDetails: true},
        });
        return this.http.get<AimoUser>(url);
    }

    getAlchoholQuotaInfo(userId: string): Observable<KitchenAlcoholInfo> {
        const url = this.occEndpoints.buildUrl('alchoholQuota', {
            urlParams: {userId},
        });
        return this.http.get<KitchenAlcoholInfo>(url);
    }

    getActiveBulletinList(category: string, id: string): Observable<AimoBulletinList> {
        const url = this.occEndpoints.buildUrl('activeBulletins', {
            queryParams: {categories: category, id: id},
        });
        return this.http.get<AimoBulletinList>(url);
    }

    getContentType(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
        });
    }

    giveConsent(userId: string): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('giveConsent', {
            urlParams: {userId},
        });
        return this.http.put(url, null);
    }

    getStoreOpeningHours(): Observable<OpeningHour[]> {
        const url = this.occEndpoints.buildUrl('storeOpeningHours');
        return this.http.get<OpeningHour[]>(url);
    }

    setHideNormalOrderTemplatesFromBasicUsers(userId: string, value: boolean): Observable<User> {
        const url = this.occEndpoints.buildUrl('hideNormalOrderTemplatesFromBasicUsers', {
            urlParams: {userId, value},
        });
        return this.http.post<User>(url, undefined);
    }

    requestForgotPasswordEmail(userEmailAddress: string): Observable<unknown> {
        const url = this.occEndpoints.buildUrl('userForgotPassword');
        let headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });
        headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        return this.recaptchaV3Service.execute('importantAction').pipe(
            switchMap((token) => {
                const httpParams: HttpParams = new HttpParams()
                    .set('userId', userEmailAddress)
                    .set('captchaToken', token);
                return this.http
                    .post(url, httpParams, {headers})
                    .pipe(catchError((error) => throwError(this.normalizeHttpError(error, this.logger))));
            }),
        );
    }

    normalizeHttpError(error: HttpErrorResponse | HttpErrorModel | any, logger: LoggerService) {
        openCloseSpinner(this.document, false);
        return tryNormalizeHttpError(error, logger);
    }
}
